import { ajax } from "lib/observable";
import { add as snackbarAdd } from "actions/snackbars";
import { IProgressive } from "reducers/base-reducers";
import { IStore } from "reducers";
import { format } from "lib/globalize-util";
import _ from "lodash";
import { Observable } from "rxjs/Observable";
import { pickGrpcError } from "helpers/epics";
import { AjaxError } from "rxjs";

export interface IHeader {
    [key: string]: string;
}

export interface IFetchParams {
    method: string;
    url: string;
    body?: any;
    retry?: number;
    headers?: IHeader;
    withCredentials?: boolean;
    crossDomain?: boolean;
}

export interface IResponse {
    response: any;
    status: number;
    xhr: XMLHttpRequest;
}

export interface IAction<T> {
    type: string;
    payload: T;
    meta?: any;
}

export interface IErrorAction {
    type: string;
    error: string | Error;
}

export interface IRequestAction<T = any> {
    url: string;
    activeBrand?: string;
    redirect?: boolean;
    next?: string;
    meta?: T;
}

export interface IRequestDataAction<T = any, M = any> extends IRequestAction<M> {
    body: T;
}

interface IExtraParams<T> {
    checkError?: (error: AjaxError, action: IAction<T>, store: IStore) => Observable<IResponse>;
    checkErrorStatus?: (response: IResponse) => void;
    debounce?: number | null;
    errorActions?: (error, action: IAction<T>, store: IStore) => any[];
    mapper?: "switchMap" | "mergeMap" | "flatMap";
}

interface IFetchExtraParams<T, V> extends IExtraParams<T> {
    /** Number of concurrent requests allowed. Default is +infinity. Should only be used with `mergeMap` */
    concurrency?: number;
    dataCustomizer?: (response, action: IAction<T>, store: IStore, xhr?: XMLHttpRequest) => V;
    finishActions?: (response, action: IAction<T>, store: IStore) => Array<IAction<V> | { type: string }>;
    nextPageGetter?: (
        response,
        action: IAction<T>,
        xhr: XMLHttpRequest
    ) => { hasNextPage?: boolean; nextPage?: string };
}

export const progressEpic =
    <T, V>(
        type: IProgressive,
        fetchParams: (action: IAction<T>, store: IStore) => IFetchParams,
        {
            dataCustomizer = (response) => response,
            finishActions = () => [],
            nextPageGetter = (_, __, xhr) => ({ nextPage: xhr.getResponseHeader("Next") }),
            checkErrorStatus = _.noop,
            checkError = (error) => Observable.throw(error),
            debounce = null,
            mapper = "switchMap",
            errorActions = () => [],
            concurrency = Number.POSITIVE_INFINITY,
        }: IFetchExtraParams<T, V> = {}
    ) =>
    (action$, store) =>
        action$
            .ofType(type.START)
            .debounce(() => (debounce != null ? Observable.timer(debounce) : Observable.of()))
            [mapper](
                (action) =>
                    ajax(fetchParams(action, store.getState()))
                        .catch((error) => checkError(error, action, store.getState()))
                        .do(defaultCheckStatus, checkErrorStatus)
                        .switchMap(({ response, xhr }) => {
                            const payload = dataCustomizer(response, action, store.getState(), xhr);
                            return [
                                {
                                    type: type.SUCCESS,
                                    payload,
                                    meta: action.meta,
                                    ...nextPageGetter?.(response, action, xhr),
                                },
                                ...finishActions(response, action, store.getState()),
                            ];
                        })
                        .takeUntil(action$.ofType(type.CANCEL))
                        .catch((error) => {
                            const actions: Array<IAction<any> | IErrorAction> = [
                                {
                                    type: type.ERROR,
                                    meta: action.meta,
                                    error: pickGrpcError(error) ? { message: pickGrpcError(error) } : error,
                                },
                            ];
                            if (error.status !== 401) {
                                // do not show unauthorized messages
                                actions.push(
                                    snackbarAdd({
                                        message:
                                            error.message ||
                                            format("Oops! Something went wrong. Please refresh and try again."),
                                    })
                                );
                            }
                            return [...actions, ...errorActions(error, action, store.getState())];
                        }),
                undefined,
                concurrency // only affects mergeMap
            );
export const baseActionEpic =
    <T, V = any>(
        type: string,
        requestParams: (action: IAction<T>, store: IStore) => IFetchParams,
        processResult: (result, action: IAction<T>, store: IStore) => Array<IAction<V> | { type: string }> = () => [],
        {
            checkErrorStatus = defaultCheckError,
            debounce = null,
            errorActions = () => [],
            mapper = "switchMap",
        }: IExtraParams<T> = {}
    ) =>
    (action$, store) =>
        action$
            .ofType(type)
            .debounce(() => (debounce != null ? Observable.timer(debounce) : Observable.of()))
            [mapper]((action) => {
                return ajax(requestParams(action, store.getState()))
                    .do(defaultCheckStatus, checkErrorStatus)
                    .flatMap((result) => processResult(result, action, store.getState()))
                    .catch((error) => [
                        snackbarAdd({
                            message:
                                error.message || format("Oops! Something went wrong. Please refresh and try again."),
                        }),
                        ...errorActions(error, action, store.getState()),
                    ]);
            });

export function defaultCheckStatus(resp: IResponse) {
    const { response, status } = resp;
    if (status < 200 || status >= 300) {
        const message =
            response && response.body && response.body.message
                ? _.upperFirst(response.body.message)
                : format("Oops! Something went wrong. Please refresh and try again.");
        throw Error(message);
    }
}

export function defaultCheckError({ xhr }) {
    const defaultMessage = format("Oops! Something went wrong. Please refresh and try again.");
    throw Error(_.get(xhr, "response.localizedMessage", _.get(xhr, "response.error", defaultMessage)));
}
